import React from "react";
import { X, Code, Settings, FileText, CheckCircle2 } from "lucide-react";

interface ChatActionPanelProps {
  viewType: string | null;
  onClose: () => void;
}

const ChatActionPanel: React.FC<ChatActionPanelProps> = ({
  viewType,
  onClose,
}) => {
  if (!viewType) return null;

  const renderView = () => {
    switch (viewType) {
      case "settings":
        return (
          <div className="space-y-6">
            <div>
              <h2 className="text-lg font-semibold text-text-primary dark:text-text-primaryDark mb-2">
                RevSync Agent Settings
              </h2>
              <p className="text-sm text-text-secondary dark:text-text-secondaryDark">
                Configure your RevSync agent settings and automation workflows.
              </p>
            </div>
            <div className="space-y-4">
              <div className="bg-background-panel dark:bg-background-panelDark rounded-lg p-4 border border-divider dark:border-divider-dark">
                <h3 className="text-sm font-semibold text-text-primary dark:text-text-primaryDark mb-2">
                  Customer Lifecycle Stage
                </h3>
                <p className="text-xs text-text-secondary dark:text-text-secondaryDark mb-3">
                  Currently set to: <strong>customer</strong>
                </p>
                <button className="px-3 py-1.5 text-xs font-medium bg-secondary hover:bg-secondary-hover text-white rounded-lg transition-colors">
                  Change Setting
                </button>
              </div>
              <div className="bg-background-panel dark:bg-background-panelDark rounded-lg p-4 border border-divider dark:border-divider-dark">
                <h3 className="text-sm font-semibold text-text-primary dark:text-text-primaryDark mb-2">
                  Auto-Create Subscriptions
                </h3>
                <p className="text-xs text-text-secondary dark:text-text-secondaryDark mb-3">
                  Enabled for Net New Deals
                </p>
                <button className="px-3 py-1.5 text-xs font-medium bg-background-default dark:bg-background-dark hover:bg-background-panel dark:hover:bg-background-panelDark border border-divider dark:border-divider-dark rounded-lg text-text-primary dark:text-text-primaryDark transition-colors">
                  Configure
                </button>
              </div>
            </div>
          </div>
        );

      case "code":
        return (
          <div className="space-y-4">
            <div>
              <h2 className="text-lg font-semibold text-text-primary dark:text-text-primaryDark mb-2">
                Code Preview
              </h2>
              <p className="text-sm text-text-secondary dark:text-text-secondaryDark">
                View the implementation code for the selected action.
              </p>
            </div>
            <div className="bg-background-panel dark:bg-background-panelDark rounded-lg border border-divider dark:border-divider-dark overflow-hidden">
              <div className="flex items-center justify-between px-4 py-2.5 bg-background-default dark:bg-background-dark border-b border-divider dark:border-divider-dark">
                <div className="flex items-center gap-2">
                  <Code className="w-4 h-4 text-text-secondary dark:text-text-secondaryDark" />
                  <span className="text-sm font-medium text-text-primary dark:text-text-primaryDark">
                    RevSyncAgentSettings.tsx
                  </span>
                </div>
              </div>
              <div className="px-4 py-4">
                <pre className="text-xs text-text-primary dark:text-text-primaryDark font-mono overflow-x-auto">
                  <code>{`const handleSaveLifecycleStage = async (selectedStage: string) => {
  setIsSavingLifecycleStage(true);
  try {
    const { data } = await revSyncService.updateCustomerLifecycleStage(selectedStage);
    setCurrentLifecycleStage(data.customerLifecycleStage);
    showToast("success", data.userMessage);
  } catch (error) {
    showToast("error", "Failed to update lifecycle stage");
  } finally {
    setIsSavingLifecycleStage(false);
  }
};`}</code>
                </pre>
              </div>
            </div>
          </div>
        );

      default:
        return (
          <div className="flex items-center justify-center h-full">
            <p className="text-sm text-text-secondary dark:text-text-secondaryDark">
              No view available
            </p>
          </div>
        );
    }
  };

  return (
    <div
      className="fixed inset-y-0 right-0 bg-background-paper dark:bg-background-paperDark shadow-2xl z-[60] flex flex-col overflow-hidden border-l border-divider dark:border-divider-dark"
      style={{
        width: "calc(100vw - 28rem)", // Full width from chat edge to screen edge
        left: "28rem", // Start after chat (max-w-md = 28rem)
        right: "0", // Extend to right edge
      }}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-divider dark:border-divider-dark bg-background-panel dark:bg-background-panelDark flex-shrink-0">
        <div className="flex items-center gap-2">
          {viewType === "settings" && (
            <Settings className="w-5 h-5 text-text-secondary dark:text-text-secondaryDark" />
          )}
          {viewType === "code" && (
            <Code className="w-5 h-5 text-text-secondary dark:text-text-secondaryDark" />
          )}
          <span className="text-sm font-semibold text-text-primary dark:text-text-primaryDark">
            {viewType === "settings" && "Settings"}
            {viewType === "code" && "Code Preview"}
          </span>
        </div>
        <button
          onClick={onClose}
          className="p-1.5 hover:bg-background-default dark:hover:bg-background-dark rounded transition-colors"
        >
          <X className="w-4 h-4 text-text-secondary dark:text-text-secondaryDark" />
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto px-6 py-6 scrollbar-hide">
        {renderView()}
      </div>
    </div>
  );
};

export default ChatActionPanel;
